
const OFFLINE_CACHE_MANIFEST_PREFIX = 'luma:approved-cache:v1:';

export interface OfflineCacheEntry {
  renderId: string;
  storyId?: string;
  householdId: string;
  listenerId: string;
  voiceId: string;
  cacheEpoch: number;
  confirmedAt: string;
  accessExpiresAt?: string;
}

function readEntries(userId: string): OfflineCacheEntry[] {
  if (!userId) return [];
  try {
    const value = JSON.parse(localStorage.getItem(OFFLINE_CACHE_MANIFEST_PREFIX + userId) || '[]');
    return Array.isArray(value) ? value : [];
  } catch { return []; }
}

export function offlineCacheEntriesForPair(
  userId: string,
  pair: { householdId: string; listenerId: string; voiceId: string },
  cacheEpoch: number | null,
): OfflineCacheEntry[] {
  if (cacheEpoch == null) return [];
  return readEntries(userId).filter((item) => (
    item.householdId === pair.householdId
    && item.listenerId === pair.listenerId
    && item.voiceId === pair.voiceId
    && item.cacheEpoch === cacheEpoch
    && typeof item.accessExpiresAt === 'string'
    && Number.isFinite(Date.parse(item.accessExpiresAt))
    && Date.now() < Date.parse(item.accessExpiresAt)
    && Number.isFinite(Date.parse(item.confirmedAt))
    && Date.now() >= Date.parse(item.confirmedAt)
  ));
}

export function markOfflineCacheConfirmed(userId: string, entry: OfflineCacheEntry): void {
  const next = readEntries(userId).filter((item) => item.renderId !== entry.renderId);
  next.push(entry);
  try { localStorage.setItem(OFFLINE_CACHE_MANIFEST_PREFIX + userId, JSON.stringify(next)); } catch { /* no-op */ }
}

export function isOfflineCacheCurrent(
  userId: string,
  renderId: string,
  householdId: string,
  listenerId: string,
  voiceId: string,
  cacheEpoch: number | null
): boolean {
  if (cacheEpoch == null) return false;
  return offlineCacheEntriesForPair(userId, { householdId, listenerId, voiceId }, cacheEpoch).some((item) => item.renderId === renderId);
}

/** A manifest acknowledgment cannot promise bytes after the browser evicts them. */
export async function pruneMissingOfflineAudio(userId: string): Promise<void> {
  if (typeof caches === 'undefined') return;
  try {
    const cache = await caches.open('luma-approved-audio-v1');
    const entries = readEntries(userId);
    const token = localStorage.getItem('auth_token');
    const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode('Bearer ' + token));
    const tokenHash = Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
    const details = await caches.open('luma-renders-api-v2');
    const detailIds = new Set((await details.keys()).map((request) => new URL(request.url))
      .filter((url) => url.searchParams.get('__session') === tokenHash)
      .map((url) => decodeURIComponent(url.pathname.split('/').pop() || '')));
    const present = await Promise.all(entries.map(async (entry) => (
      detailIds.has(entry.renderId) && await cache.match('/__luma_approved_audio__/' + encodeURIComponent(entry.renderId) + '.wav') ? entry : null
    )));
    if (localStorage.getItem('user_id') !== userId || localStorage.getItem('auth_token') !== token) return;
    localStorage.setItem(OFFLINE_CACHE_MANIFEST_PREFIX + userId, JSON.stringify(present.filter(Boolean)));
  } catch { localStorage.removeItem(OFFLINE_CACHE_MANIFEST_PREFIX + userId); }
}
